import {useState, type SubmitEvent} from "react";
import {Castle, Pencil, Trash2} from "lucide-react";
import {Button} from "@/components/ui/button.tsx";
import {Input} from "@/components/ui/input.tsx";
import {Textarea} from "@/components/ui/textarea.tsx";
import type {Annotation} from "@/dungeons/model/DungeonGeometry.ts";
import {annotationKinds} from "./AnnotationAppearance.ts";
import {useMapPopoverLayout} from "@/tabletop/useMapPopoverLayout.ts";

export default function AnnotationPopover({annotation, editable, editing, isNew, anchor, viewport, busy = false,
    error, onEdit, onSave, onDelete, onClose}: {
    annotation: Annotation;
    editable: boolean;
    editing: boolean;
    isNew: boolean;
    anchor: {x: number; y: number};
    viewport: {width: number; height: number};
    busy?: boolean;
    error?: string;
    onEdit?: () => void;
    onSave?: (annotation: Annotation) => void;
    onDelete?: () => void;
    onClose: () => void;
}) {
    const {panelRef, contentRef, style} = useMapPopoverLayout({anchor, viewport, width: editing ? 288 : 240});

    return <div className="pointer-events-none absolute inset-0 z-10">
        <div ref={panelRef} role="dialog" aria-label={editing ? "Annotation bearbeiten" : "Annotation anzeigen"}
             className="pointer-events-auto absolute overflow-y-auto rounded-lg border border-border bg-card p-3 shadow-xl"
             style={style}>
            <div ref={contentRef}>
                <div className="mb-2 flex items-start justify-between gap-2">
                    <h3 className="min-w-0 flex-1 wrap-break-word text-sm font-semibold">
                        {editing ? "Annotation bearbeiten" : annotation.title}
                    </h3>
                    {editing ? (editable && !isNew && onDelete &&
                        <Button type="button" size="icon-sm" variant="ghost"
                                className="text-destructive hover:text-destructive"
                                title="Annotation löschen" aria-label="Annotation löschen"
                                disabled={busy} onClick={onDelete}><Trash2/></Button>) : editable ?
                        <Button type="button" size="icon-sm" variant="ghost" title="Annotation bearbeiten"
                                aria-label="Annotation bearbeiten" onClick={onEdit}><Pencil/></Button> :
                        <Castle className="size-4 shrink-0 text-muted-foreground" aria-label="Dungeon-Annotation"/>}
                </div>
                {editing && editable && onSave ?
                    <AnnotationForm key={`${annotation.id}:${isNew}`} annotation={annotation} isNew={isNew}
                                    busy={busy} error={error} onSave={onSave} onCancel={onClose}/> :
                    annotation.text && <p className="whitespace-pre-wrap text-sm leading-relaxed">{annotation.text}</p>}
            </div>
        </div>
    </div>;
}

function AnnotationForm({annotation, isNew, busy, error, onSave, onCancel}: {
    annotation: Annotation;
    isNew: boolean;
    busy: boolean;
    error?: string;
    onSave: (annotation: Annotation) => void;
    onCancel: () => void;
}) {
    const [kind, setKind] = useState(annotation.kind);
    const [label, setLabel] = useState(annotation.label ?? "");
    const [title, setTitle] = useState(isNew ? "" : annotation.title);
    const [description, setDescription] = useState(annotation.text ?? "");
    const submit = (event: SubmitEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!title.trim()) return;
        onSave({...annotation, kind, label: label.trim() || null, title: title.trim(), text: description || null});
    };
    return <form className="space-y-2 text-xs" onSubmit={submit}>
        <label className="block space-y-1">Art
            <select value={kind} onChange={event => setKind(event.target.value as Annotation["kind"])}
                    className="h-8 w-full rounded-md border border-input bg-surface px-2 text-xs">
                {annotationKinds.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
        </label>
        <label className="block space-y-1">Marker-Label / Raumnummer
            <Input value={label} maxLength={20} className="h-8 px-2 text-xs"
                   onChange={event => setLabel(event.target.value)}/>
        </label>
        <label className="block space-y-1">Titel
            <Input value={title} maxLength={160} required autoFocus className="h-8 px-2 text-xs"
                   onChange={event => setTitle(event.target.value)}/>
        </label>
        <label className="block space-y-1">Beschreibung / Notiz
            <Textarea value={description} maxLength={20_000} className="min-h-24 text-sm"
                      onChange={event => setDescription(event.target.value)}/>
        </label>
        {error && <p role="alert" className="text-destructive">{error}</p>}
        <div className="flex flex-wrap gap-2">
            <Button type="submit" size="sm" disabled={busy}>Speichern</Button>
            <Button type="button" size="sm" variant="outline" disabled={busy} onClick={onCancel}>Abbrechen</Button>
        </div>
    </form>;
}
